import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import Swal from 'sweetalert2';
import { stagger, fadeUp, fadeLeft, fadeRight, viewport } from '../../utils/animations';
import {
  HiEnvelope, HiPhone, HiMapPin,
  HiArrowUpRight, HiPaperAirplane, HiCheckCircle,
} from 'react-icons/hi2';
import { RiGithubFill, RiLinkedinFill, RiFacebookFill } from 'react-icons/ri';

const MIN_SECONDS_BEFORE_SUBMIT = 4;
const SUBMIT_COOLDOWN_SECONDS = 60;

const CONTACTS = [
  { icon: HiEnvelope, label: 'Email me',  value: 'ujjalzaman@gmail.com',    href: 'mailto:ujjalzaman@gmail.com', color: '#6366f1' },
  { icon: HiPhone,   label: 'Call me',   value: '+1 (917) 000-0000',        href: 'tel:+19170000000',            color: '#8b5cf6' },
  { icon: HiMapPin,  label: 'Based in',  value: 'New York, USA', href: null,                           color: '#06b6d4' },
];

const SOCIALS = [
  { icon: RiGithubFill,   href: 'https://github.com/Ujjalzaman',           label: 'GitHub'    },
  { icon: RiLinkedinFill, href: 'https://www.linkedin.com/in/ujjal-zaman', label: 'LinkedIn'  },
  { icon: RiFacebookFill, href: 'https://www.facebook.com/ujjal.zaman',label: 'Facebook' },

];

function FloatInput({ id, name, label, type = 'text', placeholder, required }) {
  return (
    <div className="relative group">
      <label htmlFor={id}
        className="block font-mono text-[10px] text-zinc-600 uppercase tracking-[0.15em] font-bold mb-2"
      >
        {label}
      </label>
      <input
        id={id} name={name} type={type} required={required}
        placeholder={placeholder}
        className="w-full bg-white/[0.025] border border-white/[0.07] rounded-2xl px-4 py-3.5
                   text-sm text-white placeholder:text-zinc-800 outline-none
                   focus:border-indigo-500/50 focus:bg-white/[0.04] focus:ring-1 focus:ring-indigo-500/20
                   transition-all duration-200"
      />
    </div>
  );
}

export default function Contact() {
  const formRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [readyAt, setReadyAt] = useState(null);
  const [cooldownUntil, setCooldownUntil] = useState(null);

  // Allow submit only after MIN_SECONDS_BEFORE_SUBMIT from mount (anti-bot)
  useEffect(() => {
    const t = setTimeout(() => setReadyAt(Date.now()), MIN_SECONDS_BEFORE_SUBMIT * 1000);
    return () => clearTimeout(t);
  }, []);

  const canSubmit = readyAt !== null && (cooldownUntil === null || Date.now() >= cooldownUntil);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formRef.current || !canSubmit) return;

    // Honeypot: hidden field that bots fill, humans leave empty
    const honeypot = formRef.current.querySelector('[name="website_url"]');
    if (honeypot && honeypot.value.trim() !== '') {
      formRef.current.reset();
      return; // Silently reject bot submission
    }

    setSending(true);
    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      );
      setSent(true);
      formRef.current.reset();
      setCooldownUntil(Date.now() + SUBMIT_COOLDOWN_SECONDS * 1000);
      setTimeout(() => setSent(false), 5000);
    } catch {
      Swal.fire({
        icon: 'error', title: 'Oops!',
        html: 'Something went wrong. Please email me directly. <br/> <a href="mailto:ujjalzaman@gmail.com" class="text-indigo-400">ujjalzaman@gmail.com</a>',
        background: '#0a0d16', color: '#e8e8f0', confirmButtonColor: '#6366f1',
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          title: 'custom-swal-title',
          text: 'custom-swal-text',
          confirmButton: 'custom-swal-confirm-button',
        },
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 80% at 20% 60%, rgba(99,102,241,0.05) 0%, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6 sm:px-8">

        <motion.div
          variants={stagger(0.1)} initial="hidden" whileInView="show" viewport={viewport}
          className="text-center mb-16"
        >
          <motion.div variants={fadeUp} className="section-label justify-center mb-4">
            <span className="font-mono text-xs text-indigo-400 font-semibold uppercase tracking-[0.18em]">07 / Contact</span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="font-display font-extrabold text-white tracking-tight"
            style={{ fontSize: 'clamp(2.5rem, 7vw, 5.5rem)', lineHeight: 1 }}
          >
            Have a project<br />
            <span className="gradient-text">in mind?</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-base sm:text-lg text-zinc-500 max-w-xl mx-auto leading-relaxed">
            I'm open to full-time roles, freelance projects, and consulting.
            Average response time: <span className="text-zinc-300 font-medium">under 24 hours.</span>
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_1.5fr] gap-10 xl:gap-14">

          <motion.div
            variants={stagger(0.1)} initial="hidden" whileInView="show" viewport={viewport}
            className="space-y-5"
          >
            {CONTACTS.map(c => {
              const Icon = c.icon;
              const inner = (
                <motion.div
                  key={c.label}
                  variants={fadeLeft}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                  className="group flex items-center gap-4 glass rounded-2xl px-5 py-4 border border-white/[0.06] hover:border-white/15 transition-all duration-200"
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
                    style={{ background: `${c.color}14`, border: `1px solid ${c.color}28` }}
                  >
                    <Icon className="w-4.5 h-4.5" style={{ color: c.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[10px] text-zinc-700 uppercase tracking-widest font-bold">{c.label}</p>
                    <p className="text-sm font-medium text-zinc-300 mt-0.5 truncate">{c.value}</p>
                  </div>
                  {c.href && <HiArrowUpRight className="w-4 h-4 text-zinc-800 group-hover:text-indigo-400 flex-shrink-0 transition-colors" />}
                </motion.div>
              );
              return c.href
                ? <a key={c.label} href={c.href} className="block">{inner}</a>
                : <div key={c.label}>{inner}</div>;
            })}

            <motion.div variants={fadeLeft} className="glass rounded-2xl p-5 border border-white/[0.06]">
              <p className="font-mono text-[10px] text-zinc-700 uppercase tracking-widest font-bold mb-4">Find me online</p>
              <div className="flex items-center gap-3">
                {SOCIALS.map(s => {
                  const Icon = s.icon;
                  return (
                    <a
                      key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                      aria-label={s.label}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl glass border border-white/[0.06] text-zinc-500 hover:text-white hover:border-white/20 transition-all duration-200 text-sm font-medium"
                    >
                      <Icon className="w-4.5 h-4.5" />
                    </a>
                  );
                })}
              </div>
            </motion.div>

            <motion.div
              variants={fadeLeft}
              className="glass rounded-2xl p-4 border border-emerald-500/20 bg-emerald-500/[0.025] flex items-center gap-3"
            >
              <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                <span className="pulse-ring absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
              </span>
              <div>
                <p className="text-sm font-semibold text-white">Currently available</p>
                <p className="text-xs text-zinc-600">Actively seeking new opportunities</p>
              </div>
            </motion.div>
          </motion.div>

          <motion.div variants={fadeRight} initial="hidden" whileInView="show" viewport={viewport}>
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className="glass rounded-3xl p-7 sm:p-8 border border-white/[0.07] space-y-5"
              noValidate
            >
              {/* Honeypot: hidden from users, bots often fill it — leave empty */}
              <div className="absolute -left-[9999px] top-0 w-1 h-1 overflow-hidden" aria-hidden="true">
                <label htmlFor="website_url">Website (leave blank)</label>
                <input type="text" id="website_url" name="website_url" tabIndex={-1} autoComplete="off" />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <FloatInput id="user_name"  name="user_name"  label="Your name"    placeholder="John Smith" required />
                <FloatInput id="user_email" name="user_email" label="Email address" type="email" placeholder="john@company.com" required />
              </div>

              <FloatInput id="subject" name="subject" label="Subject" placeholder="Project inquiry, full-time role, consulting…" required />

              <div>
                <label htmlFor="message" className="block font-mono text-[10px] text-zinc-600 uppercase tracking-[0.15em] font-bold mb-2">
                  Message
                </label>
                <textarea
                  id="message" name="message" required rows={6}
                  placeholder="Tell me about your project, timeline, and budget…"
                  className="w-full bg-white/[0.025] border border-white/[0.07] rounded-2xl px-4 py-3.5
                             text-sm text-white placeholder:text-zinc-800 outline-none resize-none
                             focus:border-indigo-500/50 focus:bg-white/[0.04] focus:ring-1 focus:ring-indigo-500/20
                             transition-all duration-200"
                />
              </div>

              <motion.button
                type="submit"
                disabled={sending || sent || !canSubmit}
                whileHover={!sending && !sent && canSubmit ? { scale: 1.01 } : {}}
                whileTap={!sending && !sent && canSubmit ? { scale: 0.99 } : {}}
                className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-semibold text-sm text-white
                  transition-all duration-300 ${
                    sent
                      ? 'bg-emerald-600 shadow-[0_0_30px_rgba(16,185,129,0.25)]'
                      : !canSubmit
                        ? 'bg-zinc-700 cursor-not-allowed opacity-70'
                        : 'bg-indigo-600 hover:bg-indigo-500 shadow-glow-sm hover:shadow-glow-md disabled:opacity-60 disabled:cursor-not-allowed'
                  }`}
              >
                {sending ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending…
                  </>
                ) : sent ? (
                  <>
                    <HiCheckCircle className="w-5 h-5" />
                    Message sent! I'll reply soon.
                  </>
                ) : !canSubmit ? (
                  <>Please wait a moment…</>
                ) : (
                  <>
                    Send message
                    <HiPaperAirplane className="w-4 h-4" />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
